import axios, { cleanAxio, consumerAxio } from "../../../pages/AxiosConfig";
import {
  AuthModel,
  ContactUs,
  Login,
  LoginMfaResponse,
  MfaData,
  MfaResponse,
  NafathId,
  NafathIdResponse,
  Register,
  Companies,
  UserInfo,
  Organization,
  OrganizationResponse,
  UserOrganizationsResponse,
  MFAParams,
  MFAParamsWithoutCaptcha,
  VerifyMfa,
  VerifyMFAResponse,
  Waitlist,
  ForgotPasswordMfaResponse,
  ForgotPassword,
  ForgotPasswordToken,
  ResetPassword,
  PermissionResponse,
  UserInvitation,
  ExistingUserInviteAccept,
  ReferralCodeCheck,
  UserRegister,
  UserRegisterResponse,
  LoginRedirectViaEmail,
  OAuthCompleteSignupRequest,
} from "./_models";
import { performTokenRefresh } from "./tokenRefresh";

const API_URL = import.meta.env.VITE_APP_API_URL;

const GENERATE_MFA_URL = `${API_URL}/public/on-boarding/generate-identification-mfa`;
const VERIFY_MFA_URL = `${API_URL}/public/on-boarding/verify-identification-mfa`;
const GENERATE_NAFATH_MFA_URL = `${API_URL}/public/on-boarding/initiate-nafath-verification`;
const VERIFY_NAFATH_MFA_URL = `${API_URL}/public/on-boarding/check-nafath-verification-status`;
const REQUEST_PASSWORD_URL = `${API_URL}/forgot_password`;
const SEND_CONTACT_US_FORM = `${API_URL}/public/contact_us`;
const WAITLIST_URL = `${API_URL}/public/waitlist`;
const REGISTER_URL = `${API_URL}/authentication/register`;
const ACTIVE_USER_URL = `${API_URL}/user`;
const SWITCH_BRANCH_URL = `${API_URL}/authentication/switch-branch`;
const SWITCH_ORGANIZATION_URL = `${API_URL}/authentication/switch-organization`;
const GENERATE_LOGIN_MFA_URL = `${API_URL}/authentication/generate-login-mfa`;
const SUBMIT_LOGIN_MFA_URL = `${API_URL}/authentication/submit-login-mfa`;
const ONBOARDING_ACCOUNTS_URL = `${API_URL}/user/related-companies`;
const USER_ORGANIZATIONS_URL = `${API_URL}/user/organizations`;
const SAVE_ORGANIZATION_URL = `${API_URL}/organization`;
const GENERATE_FORGOT_PASSWORD_MFA_URL = `${API_URL}/authentication/forgot-password`;
const SUBMIT_FORGOTPASSWORD_MFA_URL = `${API_URL}/authentication/verify-forgot-password`;
const RESET_PASSWORD_URL = `${API_URL}/authentication/reset-password`;
const PERMISSIONS_URL = `${API_URL}/authorization/permissions`;
const REGISTER_INVITE_USER_URL = `${API_URL}/authentication/register-invited-user`;
const REGISTER_USER = `${API_URL}/authentication/user/register`;
const VERIFY_REGISTER_USER = `${API_URL}/authentication/user/verify`;
const REFERRAL_CODE = `${API_URL}/referral/codes`;
// Plain browser navigation target (not called via axios) — the backend redirects
// straight to Google, then back to our /authentication/oauth/google/callback, then
// finally to this app's /auth/complete-profile with a status query param.
export const GOOGLE_OAUTH_AUTHORIZE_URL = `${API_URL}/authentication/oauth/google/authorize`;
const OAUTH_COMPLETE_SIGNUP_URL = `${API_URL}/authentication/oauth/complete-signup`;

// Server should return AuthModel
export function generateLoginMFA(data: Login) {
  const params = {
    email: data.email,
    password: data.password,
  };
  if (data.delivery_method) {
    Object.assign(params, { delivery_method: data.delivery_method });
  }
  return cleanAxio.post<LoginMfaResponse>(GENERATE_LOGIN_MFA_URL, params, {
    headers: { "X-G-Recaptcha-Token": data["g-recaptcha-response"] },
  });
}

export function LoginUsingEmailRedirect(data: LoginRedirectViaEmail) {
  return cleanAxio.get<AuthModel>(
    VERIFY_REGISTER_USER +
      "?user_id=" +
      data.user_id +
      "&verification_code=" +
      data.verification_code,
    { headers: { "X-G-Recaptcha-Token": data["g-recaptcha-response"] } }
  );
}

export function submitLoginMFA(data: Login, mfaData: MfaData) {
  return axios.post<AuthModel>(
    SUBMIT_LOGIN_MFA_URL,
    {
      email: data.email,
      password: data.password,
      mfa_id: mfaData.mfa_id,
      mfa: mfaData.mfa,
    },
    { headers: { "X-G-Recaptcha-Token": data["g-recaptcha-response"] } }
  );
}

export function refreshToken() {
  return performTokenRefresh().then((auth) => ({ data: auth }));
}

export function switchBranch(new_id: string | null) {
  const params = {
    branch_id: new_id,
  };
  return axios.post(SWITCH_BRANCH_URL, params);
}

export function switchOrganization(organization_id: string) {
  return axios.post<AuthModel>(SWITCH_ORGANIZATION_URL, { organization_id });
}

export function getUserOrganizations() {
  return axios.get<UserOrganizationsResponse>(USER_ORGANIZATIONS_URL);
}

export function generateMFA(data: MFAParams) {
  const param: MFAParamsWithoutCaptcha = {};
  if (data.phone_number) {
    param["phone_number"] = "+966" + data.phone_number;
  }
  if (data.email) {
    param["email"] = data.email;
  }
  return cleanAxio.post<MfaResponse>(GENERATE_MFA_URL, param, {
    headers: { "X-G-Recaptcha-Token": data["g-recaptcha-response"] },
  });
}

export function verifyMFA(data: VerifyMfa) {
  return axios.post<VerifyMFAResponse>(
    VERIFY_MFA_URL,
    {
      identification_id: data.identification_id,
      mfa: data.mfa,
    },
    { headers: { "X-G-Recaptcha-Token": data["g-recaptcha-response"] } }
  );
}

export function generateNafathMFA(data: NafathId) {
  return cleanAxio.post<NafathIdResponse>(
    GENERATE_NAFATH_MFA_URL,
    {
      identification: data.nafathId,
    },
    { headers: { "X-G-Recaptcha-Token": data["g-recaptcha-response"] } }
  );
}

export function verifyNafathMFA(data: NafathId) {
  return cleanAxio.post<{ status: string }>(
    VERIFY_NAFATH_MFA_URL,
    {
      nafath_id: data.nafathUUID,
    },
    { headers: { "X-G-Recaptcha-Token": data["g-recaptcha-response"] } }
  );
}

export function register(data: Register) {
  return axios.post<AuthModel>(
    REGISTER_URL,
    {
      national_id: data.national_id,
      email: data.email,
      password: data.password,
      phone_number: "+966" + data.phone_number,
      identification_id: data.identification_id,
      first_name: data.first_name,
      last_name: data.last_name,
    },
    { headers: { "X-G-Recaptcha-Token": data["g-recaptcha-response"] } }
  );
}

export function saveOrganization(data: Organization) {
  return axios.post<OrganizationResponse>(SAVE_ORGANIZATION_URL, {
    name: data.name,
    commercial_registration: data.commercial_registration,
    entity_number: data.entity_number,
  });
}

export function getOnboardingAccounts() {
  return axios.get<Companies>(ONBOARDING_ACCOUNTS_URL);
}

export function getUserInfo() {
  return axios.get<UserInfo>(ACTIVE_USER_URL);
}

export function requestPassword(email: string) {
  return axios.post<{ result: boolean }>(REQUEST_PASSWORD_URL, {
    email,
  });
}

export function sentContactUs(contactus: ContactUs) {
  return axios.post<{ result: boolean }>(
    SEND_CONTACT_US_FORM,
    {
      email: contactus.email,
      name: contactus.name,
      message: contactus.message,
    },
    { headers: { "X-G-Recaptcha-Token": contactus["g-recaptcha-response"] } }
  );
}

export function submitJoinWaitlist(waitlist: Waitlist) {
  return axios.post(
    WAITLIST_URL,
    {
      phone_number: "+966" + waitlist.phone,
    },
    { headers: { "X-G-Recaptcha-Token": waitlist["g-recaptcha-response"] } }
  );
}

export function submitJoinWaitlistDetails(waitlist: Waitlist) {
  const params: Waitlist = {
    name: waitlist.name,
    company_name: waitlist.company_name,
    role: waitlist.role,
    company_size: waitlist.company_size,
    how_did_you_hear: waitlist.how_did_you_hear,
  };
  if (waitlist.email) {
    params.email = waitlist.email;
  }
  return axios.put(WAITLIST_URL + "/" + waitlist.id, params, {
    headers: { "X-G-Recaptcha-Token": waitlist["g-recaptcha-response"] },
  });
}

export function generateForgotPasswordMFA(data: ForgotPassword) {
  return cleanAxio.post<ForgotPasswordMfaResponse>(
    GENERATE_FORGOT_PASSWORD_MFA_URL,
    {
      email: data.email,
    },
    { headers: { "X-G-Recaptcha-Token": data["g-recaptcha-response"] } }
  );
}

export function submitForgotPasswordMFA(mfaData: MfaData, recaptcha: string) {
  return cleanAxio.post<ForgotPasswordToken>(
    SUBMIT_FORGOTPASSWORD_MFA_URL,
    {
      mfa_id: mfaData.mfa_id,
      mfa: mfaData.mfa,
    },
    { headers: { "X-G-Recaptcha-Token": recaptcha } }
  );
}

export function resetPassword(data: ResetPassword) {
  return cleanAxio.post(
    RESET_PASSWORD_URL,
    {
      reset_password_token: data.reset_password_token,
      new_password: data.new_password,
    },
    { headers: { "X-G-Recaptcha-Token": data["g-recaptcha-response"] } }
  );
}

export function registerInviteUser(data: UserInvitation) {
  return cleanAxio.post(REGISTER_INVITE_USER_URL, data, {
    headers: { "X-G-Recaptcha-Token": data["g-recaptcha-response"] },
  });
}

// Signed-in-user accept path for the same endpoint — must go through the
// token-bearing `axios` instance (not `cleanAxio`) since the backend requires
// the invited user's Bearer token to match the invitation's email.
export function acceptInviteAsExistingUser(data: ExistingUserInviteAccept) {
  return axios.post<AuthModel>(REGISTER_INVITE_USER_URL, data, {
    headers: { "X-G-Recaptcha-Token": data["g-recaptcha-response"] },
  });
}

export function registerUser(data: UserRegister) {
  return cleanAxio.post<UserRegisterResponse>(REGISTER_USER, data, {
    headers: { "X-G-Recaptcha-Token": data["g-recaptcha-response"] },
  });
}

export function checkReferralCode(code: string) {
  return cleanAxio.get<ReferralCodeCheck>(
    `${REFERRAL_CODE}/${encodeURIComponent(code)}`
  );
}

export function resendRegisterUserEmail(id: string, recaptcha: string) {
  return cleanAxio.get(VERIFY_REGISTER_USER + "/resend?user_id=" + id, {
    headers: { "X-G-Recaptcha-Token": recaptcha },
  });
}
// user_id + verification_code (from the OAuth callback redirect) are this
// request's only auth -- there's no session yet for an account that was never
// fully created. See GoogleCompleteProfile.tsx.
export function completeOAuthSignup(data: OAuthCompleteSignupRequest) {
  return cleanAxio.post<AuthModel>(OAUTH_COMPLETE_SIGNUP_URL, data, {
    headers: { "X-G-Recaptcha-Token": data["g-recaptcha-response"] },
  });
}

export function verifyUserEmail(id: string, recaptcha: string) {
  return cleanAxio.post(
    VERIFY_REGISTER_USER,
    {
      user_id: id,
    },
    {
      headers: { "X-G-Recaptcha-Token": recaptcha },
    }
  );
}

export function getPermissions() {
  return axios.get<PermissionResponse>(PERMISSIONS_URL);
}

const CONVERSION_RATES_URL = `${API_URL}/consumer_portal/conversion-rates`;

export function getConversionRates() {
  return consumerAxio.get<{ conversion_rates: Record<string, string> }>(
    CONVERSION_RATES_URL
  );
}
